import type { ClientFeatureMap } from "@/lib/resolveClientFeatures";
import { Link } from "react-router-dom";

interface Props {
  features: ClientFeatureMap;
  /** Portal token for deep links */
  portalToken: string;
  /** Whether client already has an invitation record */
  hasInvitation?: boolean;
  /** Whether client already has a signature book record */
  hasSignatureBook?: boolean;
  brandColor?: string;
}

interface FeatureItem {
  key: keyof ClientFeatureMap;
  label: string;
  description: string;
  icon: string;
  activeColor: string;
  activeBg: string;
  activeBorder: string;
  /** Optional deep link path appended to /portal/:token */
  linkPath?: string;
}

const FEATURES: FeatureItem[] = [
  {
    key:          "digital_invitation",
    label:        "Digital Invitation",
    description:  "Interactive invite with RSVP, countdown & gallery",
    icon:         "ri-mail-open-line",
    activeColor:  "text-[#C8A96E]",
    activeBg:     "bg-[#C8A96E]/10",
    activeBorder: "border-[#C8A96E]/30",
    linkPath:     "/invitation",
  },
  {
    key:          "signature_book",
    label:        "Signature Book",
    description:  "Guest messages on photos + downloadable PDF",
    icon:         "ri-book-open-line",
    activeColor:  "text-amber-600",
    activeBg:     "bg-amber-50",
    activeBorder: "border-amber-200",
    linkPath:     "/signature-book",
  },
  {
    key:          "photo_selection",
    label:        "Photo Selection",
    description:  "Choose your favourite photos from the shoot",
    icon:         "ri-gallery-line",
    activeColor:  "text-sky-600",
    activeBg:     "bg-sky-50",
    activeBorder: "border-sky-200",
  },
  {
    key:          "final_deliveries",
    label:        "Final Deliveries",
    description:  "High-res edited photos delivered digitally",
    icon:         "ri-send-plane-line",
    activeColor:  "text-emerald-600",
    activeBg:     "bg-emerald-50",
    activeBorder: "border-emerald-200",
  },
  {
    key:          "dvd_online",
    label:        "Video / Media",
    description:  "Event video, streaming & physical media packages",
    icon:         "ri-film-line",
    activeColor:  "text-rose-500",
    activeBg:     "bg-rose-50",
    activeBorder: "border-rose-200",
  },
  {
    key:          "appointments",
    label:        "Appointments",
    description:  "Book meetings & consultations with your studio",
    icon:         "ri-calendar-check-line",
    activeColor:  "text-indigo-500",
    activeBg:     "bg-indigo-50",
    activeBorder: "border-indigo-200",
  },
];

export default function ActiveFeaturesWidget({
  features,
  portalToken,
  hasInvitation,
  hasSignatureBook,
  brandColor = "#C8A96E",
}: Props) {
  const activeCount = FEATURES.filter((f) => features[f.key]).length;

  // Don't render at all if no features are active and none are available
  // (empty state — means the client has zero addons)
  const hasAnyActive = activeCount > 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="w-5 h-5 flex items-center justify-center" style={{ color: brandColor }}>
            <i className="ri-sparkling-line text-base"></i>
          </span>
          <h2 className="text-[#0f172a] font-semibold text-sm">My Active Features</h2>
        </div>
        {hasAnyActive && (
          <span className="bg-emerald-50 border border-emerald-200 text-emerald-600 text-[10px] font-semibold px-2.5 py-0.5 rounded-full">
            {activeCount} active
          </span>
        )}
      </div>

      {/* Feature grid */}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {FEATURES.map((feature) => {
          const isActive = features[feature.key];

          // Build deep link href
          let href: string | undefined;
          if (isActive && feature.linkPath) {
            if (feature.key === "digital_invitation" && !hasInvitation) {
              href = undefined; // no link if no invitation created yet
            } else if (feature.key === "signature_book" && !hasSignatureBook) {
              href = undefined; // no link if no book created yet
            } else {
              href = `/portal/${portalToken}${feature.linkPath}`;
            }
          }

          if (isActive) {
            return (
              <FeatureCardActive
                key={feature.key}
                feature={feature}
                href={href}
              />
            );
          }

          return (
            <FeatureCardInactive key={feature.key} feature={feature} />
          );
        })}
      </div>

      {/* Empty state — no features at all */}
      {!hasAnyActive && (
        <div className="px-5 pb-5 text-center">
          <p className="text-slate-400 text-xs">
            No extra features activated yet. Contact your photographer to learn more.
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Active feature card ───────────────────────────────────── */

interface FeatureCardActiveProps {
  feature: FeatureItem;
  href?: string;
}

function FeatureCardActive({ feature, href }: FeatureCardActiveProps) {
  const inner = (
    <div
      className={`
        relative flex items-start gap-3 rounded-xl p-3.5 border
        ${feature.activeBg} ${feature.activeBorder}
        transition-all
        ${href ? "cursor-pointer hover:shadow-sm" : ""}
      `}
    >
      {/* Active dot */}
      <span className="absolute top-2.5 right-2.5 w-1.5 h-1.5 rounded-full bg-emerald-400"></span>

      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${feature.activeBg} border ${feature.activeBorder}`}
      >
        <i className={`${feature.icon} text-base ${feature.activeColor}`}></i>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className={`text-xs font-semibold ${feature.activeColor} leading-tight`}>
            {feature.label}
          </p>
        </div>
        <p className="text-slate-400 text-[11px] leading-tight line-clamp-2">
          {feature.description}
        </p>
        {href && (
          <span className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold ${feature.activeColor}`}>
            Open <i className="ri-arrow-right-line text-[10px]"></i>
          </span>
        )}
      </div>
    </div>
  );

  if (href) {
    return <Link to={href}>{inner}</Link>;
  }
  return inner;
}

/* ─── Inactive feature card ─────────────────────────────────── */

interface FeatureCardInactiveProps {
  feature: FeatureItem;
}

function FeatureCardInactive({ feature }: FeatureCardInactiveProps) {
  return (
    <div className="flex items-start gap-3 rounded-xl p-3.5 border border-dashed border-slate-200 bg-slate-50/50">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-slate-100">
        <i className={`${feature.icon} text-base text-slate-300`}></i>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-slate-400 leading-tight mb-0.5">
          {feature.label}
        </p>
        <p className="text-slate-300 text-[11px] leading-tight">
          Available through your photographer
        </p>
      </div>
    </div>
  );
}